import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/api";
import { FileStack, ScrollText, Percent, Wallet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

function KPI({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="border border-border bg-white p-5 rounded-md shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-caps text-muted-foreground font-bold">{label}</div>
          <div className="mt-2 text-3xl font-black font-mono tracking-tight">{value}</div>
          {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
        </div>
        {Icon && <Icon size={20} className="text-[#C6A15B]" />}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [k, setK] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const { tenant } = useAuth();
  const currency = tenant?.currency || "EUR";

  const [request, setRequest] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setLoading(true); setError("");
    (async () => {
      try {
        const [{ data: kpi }, { data: rlist }] = await Promise.all([
          api.get("/dashboard/kpis"), api.get("/rfqs"),
        ]);
        if (!kpi || typeof kpi !== "object" || Array.isArray(kpi) || !Array.isArray(rlist)) throw new Error("Invalid dashboard response");
        if (live) { setK(kpi); setRfqs(rlist); }
      } catch (_) {
        if (live) setError("Impossibile caricare la panoramica. I dati non sono disponibili: riprova.");
      } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [request]);

  return (
    <div className="p-4 sm:p-8" data-testid="dashboard">
      <div className="flex flex-wrap gap-4 items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-caps font-bold text-muted-foreground">Panoramica officina</div>
          <h1 className="text-3xl font-black tracking-tight mt-1">Dashboard</h1>
        </div>
        <Link
          to="/rfqs/new"
          data-testid="btn-new-rfq"
          className="bg-primary text-primary-foreground px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md shadow-sm hover:bg-[#E0C27A] border border-[#B5914B]/60"
        >
          + Nuovo preventivo
        </Link>
      </div>

      {loading && <p role="status" data-testid="dashboard-loading">Caricamento della panoramica…</p>}
      {!loading && error && <div role="alert" className="border border-border rounded-md bg-white p-5" data-testid="dashboard-error"><p>{error}</p><button type="button" className="mt-3 min-h-11 rounded-md bg-primary text-primary-foreground px-4 py-2" onClick={() => setRequest(n => n + 1)}>Riprova</button></div>}
      {!loading && !error && <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Richieste ricevute" value={k?.rfqs_total ?? "—"} sub="Richieste operative" icon={FileStack} testId="kpi-rfqs" />
        <KPI label="Da revisionare" value={k?.needs_review ?? "—"} sub="In attesa di verifica" icon={AlertTriangle} testId="kpi-needs-review" />
        <KPI label="Preventivi totali" value={k?.quotes_total ?? "—"} sub="Bozze e approvati" icon={ScrollText} testId="kpi-quotes" />
        <KPI label="Tasso accettazione" value={k?.acceptance_rate == null ? "—" : `${k.acceptance_rate}%`} sub="Preventivi accettati / inviati" icon={CheckCircle2} testId="kpi-acceptance" />
        <KPI label="Valore preventivato" value={k?.total_quoted_value == null ? "—" : fmtMoney(k.total_quoted_value, currency)} sub="Somma linee preventivo" icon={Wallet} testId="kpi-value" />
        <KPI label="Margine medio" value={k?.avg_margin == null ? "—" : `${k.avg_margin}%`} sub="Media pesata" icon={Percent} testId="kpi-margin" />
      </div>

      <div className="mt-8 border border-border bg-white rounded-md shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="font-extrabold text-xs uppercase tracking-caps">Ultime richieste</div>
          <Link to="/rfqs" className="text-xs font-semibold link-tech hover:underline" data-testid="link-view-all-rfqs">Vedi tutte →</Link>
        </div>
        <div tabIndex={0} role="region" aria-label="Ultime richieste: tabella scorrevole" className="overflow-x-auto">
        <table className="dense-table w-full">
          <thead>
            <tr>
              <th>Riferimento</th><th>Cliente</th><th>Stato</th><th>Affidabilità</th><th>Creata</th>
            </tr>
          </thead>
          <tbody>
            {rfqs.slice(0, 8).map((r) => (
              <tr key={r.id} data-testid={`row-rfq-${r.id}`}>
                <td className="font-mono"><Link to={`/rfqs/${r.id}`} className="hover:underline">{r.reference}</Link></td>
                <td>{r.customer_name}</td>
                <td><span className="uppercase text-[10px] tracking-widest">{r.status}</span></td>
                <td>
                  <span className={`inline-block px-2 py-0.5 border text-[10px] uppercase tracking-widest bg-reliability-${r.reliability || "red"}`}>
                    {r.reliability || "red"}
                  </span>
                </td>
                <td className="text-muted-foreground text-xs">{(r.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rfqs.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-muted-foreground text-sm" data-testid="dashboard-empty">Nessuna richiesta operativa. <Link to="/rfqs/new" className="underline" data-testid="dashboard-empty-new-quote">Nuovo preventivo</Link></td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      </>}
    </div>
  );
}

